import React, { useEffect, useMemo, useState } from "react";
import { VendorsService } from "../../api/generated";
import { withApiData } from "../../api/openapiClient";
import { useAuth } from "../../useAuth";
import { RoleEnum, isRole } from "../../constants/roles";
import Loader from "../../components/common/Loader";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Table from "../../ui/Table";
import { spacing } from "../../ui/tokens";

const emptyCardStyle = {
  border: "1px dashed #d1d5db",
  borderRadius: 12,
  padding: spacing.xl,
  background: "#f9fafb",
  display: "grid",
  gap: spacing.sm,
  maxWidth: 520,
};

export default function VendorsRegister() {
  const { user } = useAuth();
  const canEdit = isRole(user?.role, RoleEnum.ADMIN);

  const [vendors, setVendors] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const loadVendors = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await withApiData(VendorsService.listVendorsVendorsGet());
      setVendors(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVendors();
  }, []);

  useEffect(() => {
    const handler = () => setShowForm(true);
    window.addEventListener("openAddVendor", handler);
    return () => window.removeEventListener("openAddVendor", handler);
  }, []);

  const filteredVendors = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return vendors;
    return vendors.filter((vendor) => {
      const nameMatch = vendor.name?.toLowerCase().includes(term);
      const descMatch = vendor.description?.toLowerCase().includes(term);
      return nameMatch || descMatch;
    });
  }, [vendors, query]);

  const columns = useMemo(
    () => [
      { header: "Name", accessor: "name" },
      {
        header: "Description",
        accessor: (row) => row.description || "No description",
      },
    ],
    []
  );

  const handleCreate = async (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    setSaveError(null);
    try {
      await withApiData(
        VendorsService.createVendorVendorsPost({
          requestBody: {
            name: trimmed,
            description: description.trim() || null,
          },
        })
      );
      setName("");
      setDescription("");
      setShowForm(false);
      await loadVendors();
    } catch (err) {
      setSaveError(err?.message || "Failed to create vendor");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: spacing.lg }}>
      <div>
        <h2 style={{ marginBottom: 6 }}>Vendors</h2>
        <p style={{ marginTop: 0, color: "#6b7280", fontSize: 14 }}>
          Track third parties so controls can be linked to vendor coverage and oversight.
        </p>
      </div>

      <div style={{ display: "flex", gap: spacing.md, flexWrap: "wrap", alignItems: "center" }}>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search vendors"
          style={{ minWidth: 220 }}
        />
        <Button variant="secondary" onClick={loadVendors}>
          Refresh
        </Button>
        {canEdit && (
          <Button variant="primary" onClick={() => setShowForm(true)}>
            Add Vendor
          </Button>
        )}
      </div>

      {canEdit && showForm && (
        <form
          onSubmit={handleCreate}
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: spacing.lg,
            display: "grid",
            gap: spacing.md,
            background: "#ffffff",
            maxWidth: 640,
          }}
        >
          <div style={{ fontWeight: 600 }}>Add a new vendor</div>
          <div style={{ display: "grid", gap: spacing.sm }}>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Vendor name" />
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
            />
          </div>
          {saveError && <div style={{ color: "#b91c1c" }}>{saveError}</div>}
          <div style={{ display: "flex", gap: spacing.sm }}>
            <Button type="submit" disabled={!name.trim()} loading={saving}>
              Create Vendor
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {loading && <Loader label="Loading vendors..." />}
      {error && <div style={{ color: "#b91c1c" }}>{error}</div>}

      {!loading && !error && vendors.length === 0 && (
        <div style={emptyCardStyle}>
          <div style={{ fontWeight: 600 }}>No vendors yet</div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            Vendors show external dependencies so controls can be linked to oversight and assurance.
          </div>
          {canEdit ? (
            <Button onClick={() => setShowForm(true)}>Add your first vendor</Button>
          ) : (
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              Contact an administrator to add vendors.
            </div>
          )}
        </div>
      )}

      {!loading && !error && vendors.length > 0 && (
        <Table
          columns={columns}
          data={filteredVendors}
          emptyState="No matching vendors."
          getRowKey={(row) => row.id}
        />
      )}
    </div>
  );
}
